/**
 * VendorApiAdapter — Frontend Input Adapter (Driving/Primary)
 *
 * Implements the VendorPort using axios over the REST API.
 * This is the ONLY file on the frontend that knows:
 *  - That we use axios
 *  - What the API URL structure looks like
 *
 * Wired into useVendors.ts (the hook layer), never imported by components directly.
 */

import type { VendorPort } from '../../application/ports/VendorPort';
import { adminConsoleClient } from '../../api/adminConsoleClient';
import type {
  VendorMapping,
  TestResult,
  LiveTestRequest,
  LiveTestResult,
  CreateVendorRequest,
  UpdateVendorRequest,
  TestConnectionRequest,
  VendorServiceRouting,
  RoutingServiceKey,
  VendorMappingSaveResponse,
  VendorDeleteResult,
  VendorMappingsExportResult,
  VendorServiceRoutingSaveResponse,
  ThirdPartyConfigSyncResult,
  ThirdPartyConfigSyncStatus,
  AdapterConfigRoutingSnapshot,
  AdapterConfigRoutingUpdateResponse,
} from '../../domain/vendor';

const BASE = '/vendors';

function readResponseHeader(headers: Record<string, unknown>, name: string): string | undefined {
  const lower = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower && v !== undefined && v !== null) {
      return String(v);
    }
  }
  return undefined;
}

function parseExportFromDeleteHeaders(headers: Record<string, unknown>): VendorMappingsExportResult {
  const status = readResponseHeader(headers, 'x-vendor-mappings-export-status');
  if (String(status ?? '').toLowerCase() === 'ok') {
    return { ok: true };
  }
  const err = readResponseHeader(headers, 'x-vendor-mappings-export-error');
  return { ok: false, error: err?.slice(0, 500) || 'vendorMappings.json export failed after delete' };
}

function parseSyncFromDeleteHeaders(headers: Record<string, unknown>): ThirdPartyConfigSyncResult | undefined {
  const status = readResponseHeader(headers, 'x-third-party-config-sync-status');
  if (!status) return undefined;
  if (String(status).toLowerCase() === 'ok') {
    return {
      ok: true,
      label: '',
      configKey: '',
      vendorCount: 0,
      syncedAt: new Date().toISOString(),
    };
  }
  const err = readResponseHeader(headers, 'x-third-party-config-sync-error');
  return {
    ok: false,
    label: '',
    configKey: '',
    attemptedAt: new Date().toISOString(),
    error: err?.slice(0, 500) || 'third-party adapter config sync failed after delete',
  };
}

export const vendorApiAdapter: VendorPort = {
  async listVendors(): Promise<VendorMapping[]> {
    const res = await adminConsoleClient.get<VendorMapping[]>(BASE);
    return res.data;
  },

  async createVendor(request: CreateVendorRequest): Promise<VendorMappingSaveResponse> {
    const res = await adminConsoleClient.post<VendorMappingSaveResponse>(BASE, request);
    return res.data;
  },

  async updateVendor(vendorId: string, request: UpdateVendorRequest): Promise<VendorMappingSaveResponse> {
    const res = await adminConsoleClient.put<VendorMappingSaveResponse>(
      `${BASE}/${encodeURIComponent(vendorId)}`,
      request,
    );
    return res.data;
  },

  async deleteVendor(vendorId: string): Promise<VendorDeleteResult> {
    const res = await adminConsoleClient.delete<VendorDeleteResult | undefined>(`${BASE}/${encodeURIComponent(vendorId)}`);
    if (res.data?.vendorMappingsExport) return res.data;
    return {
      vendorMappingsExport: parseExportFromDeleteHeaders(res.headers as Record<string, unknown>),
      thirdPartyConfigSync: parseSyncFromDeleteHeaders(res.headers as Record<string, unknown>),
    };
  },

  async testConnection(request: TestConnectionRequest): Promise<TestResult> {
    const res = await adminConsoleClient.post<TestResult>(`${BASE}/test-connection`, request);
    return res.data;
  },

  async testVendorConnection(vendorId: string): Promise<TestResult> {
    const res = await adminConsoleClient.post<TestResult>(`${BASE}/${encodeURIComponent(vendorId)}/test`);
    return res.data;
  },

  async liveTest(request: LiveTestRequest): Promise<LiveTestResult> {
    const res = await adminConsoleClient.post<LiveTestResult>(`${BASE}/live-test`, request);
    return res.data;
  },

  async getServiceRouting(serviceName: RoutingServiceKey): Promise<VendorServiceRouting> {
    const res = await adminConsoleClient.get<VendorServiceRouting>(`${BASE}/service-routing/${encodeURIComponent(serviceName)}`);
    return res.data;
  },

  async updateServiceRouting(
    serviceName: RoutingServiceKey,
    request: VendorServiceRouting,
  ): Promise<VendorServiceRoutingSaveResponse> {
    const res = await adminConsoleClient.put<VendorServiceRoutingSaveResponse>(
      `${BASE}/service-routing/${encodeURIComponent(serviceName)}`,
      request,
    );
    return res.data;
  },

  async getAdapterConfigRouting(label: string): Promise<AdapterConfigRoutingSnapshot> {
    const res = await adminConsoleClient.get<AdapterConfigRoutingSnapshot>(`${BASE}/adapter-config-routing`, {
      params: { label: label.trim() || 'default' },
    });
    return res.data;
  },

  async updateAdapterConfigRouting(
    serviceKey: RoutingServiceKey,
    label: string,
    vendors: VendorServiceRouting['vendors'],
  ): Promise<AdapterConfigRoutingUpdateResponse> {
    const res = await adminConsoleClient.put<AdapterConfigRoutingUpdateResponse>(
      `${BASE}/adapter-config-routing/${encodeURIComponent(serviceKey)}`,
      { label: label.trim() || 'default', vendors },
    );
    return res.data;
  },

  async getThirdPartyConfigSyncStatus(): Promise<ThirdPartyConfigSyncStatus> {
    const res = await adminConsoleClient.get<ThirdPartyConfigSyncStatus>(`${BASE}/third-party-sync`);
    return res.data;
  },

  async syncThirdPartyConfig(configLabel?: string): Promise<ThirdPartyConfigSyncResult> {
    const res = await adminConsoleClient.post<ThirdPartyConfigSyncResult>(
      `${BASE}/third-party-sync`,
      {},
      configLabel?.trim()
        ? { params: { label: configLabel.trim() } }
        : undefined,
    );
    return res.data;
  },
};
